import { useState } from 'react'
import { Button } from '@web/components/ui/Button'
import { useUpdateVocabulary, useVocabularySearch, type AdminVocabRow } from '@web/lib/admin'

/**
 * Fix a translation, add a usage note, flag a word as trip-critical.
 *
 * This is the edit a native speaker actually makes — spotting that an English
 * gloss is subtly off, or that a phrase needs a "this is informal" note. Bulk
 * authoring stays in the seed files.
 */
export function VocabularyEditor() {
  const [query, setQuery] = useState('')
  const { data, isPending } = useVocabularySearch(query)
  const [editing, setEditing] = useState<AdminVocabRow | null>(null)

  return (
    <>
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search Portuguese or English…"
        aria-label="Search vocabulary"
        autoCapitalize="none"
        spellCheck={false}
        className="mb-4 min-h-11 w-full max-w-md rounded-xl bg-white px-4 text-base ring-1 ring-sand-200 focus:ring-2 focus:ring-brand-600"
      />

      {isPending && <p className="text-ink-500">Loading…</p>}

      <ul className="space-y-2">
        {(data?.vocabulary ?? []).map((row) => (
          <li
            key={row.id}
            className="flex items-center gap-3 rounded-card bg-white p-4 ring-1 ring-brand-900/5"
          >
            <div className="flex-1">
              <p className="flex items-center gap-2">
                <span className="font-semibold text-ink-900" lang="pt-BR">
                  {row.pt}
                </span>
                {row.trip_critical === 1 && (
                  <span className="text-gold-500" title="Essential">
                    ★
                  </span>
                )}
                {row.edited_at && (
                  <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">
                    edited
                  </span>
                )}
              </p>
              <p className="text-sm text-ink-700">{row.en}</p>
            </div>
            <Button variant="secondary" onClick={() => setEditing(row)}>
              Edit
            </Button>
          </li>
        ))}
      </ul>

      {editing && <VocabModal row={editing} onClose={() => setEditing(null)} />}
    </>
  )
}

function VocabModal({ row, onClose }: { row: AdminVocabRow; onClose: () => void }) {
  const update = useUpdateVocabulary()
  const [form, setForm] = useState({
    pt: row.pt,
    en: row.en,
    examplePt: row.example_pt ?? '',
    exampleEn: row.example_en ?? '',
    regionNote: row.region_note ?? '',
    tripCritical: row.trip_critical === 1,
    category: row.category,
    difficulty: row.difficulty,
  })

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  function save() {
    update.mutate(
      {
        id: row.id,
        ...form,
        // Empty strings mean "no value", not an empty note.
        examplePt: form.examplePt.trim() || null,
        exampleEn: form.exampleEn.trim() || null,
        regionNote: form.regionNote.trim() || null,
      },
      { onSuccess: onClose },
    )
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Edit word"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-900/40 p-4 backdrop-blur-sm"
    >
      <div className="my-8 w-full max-w-lg rounded-card bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between">
          <h2 className="font-display text-xl font-bold text-ink-900">Edit word</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-2xl leading-none text-ink-400 hover:text-ink-900"
          >
            ×
          </button>
        </div>

        <div className="space-y-3">
          <Field label="Portuguese" value={form.pt} onChange={(v) => set('pt', v)} lang="pt-BR" />
          <Field label="English" value={form.en} onChange={(v) => set('en', v)} />
          <Field
            label="Example (Portuguese)"
            value={form.examplePt}
            onChange={(v) => set('examplePt', v)}
            lang="pt-BR"
          />
          <Field
            label="Example (English)"
            value={form.exampleEn}
            onChange={(v) => set('exampleEn', v)}
          />
          <Field
            label="Usage note"
            value={form.regionNote}
            onChange={(v) => set('regionNote', v)}
          />

          <div className="flex gap-4">
            <Field
              label="Category"
              value={form.category}
              onChange={(v) => set('category', v)}
              className="flex-1"
            />
            <div className="w-28">
              <label className="mb-1.5 block text-sm font-medium text-ink-700">Difficulty</label>
              <input
                type="number"
                min={1}
                max={5}
                value={form.difficulty}
                onChange={(e) => set('difficulty', Number(e.target.value))}
                className="min-h-11 w-full rounded-xl bg-white px-3 ring-1 ring-sand-200 focus:ring-2 focus:ring-brand-600"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={form.tripCritical}
              onChange={(e) => set('tripCritical', e.target.checked)}
              className="size-4"
            />
            Essential for the trip (reviewed more often)
          </label>
        </div>

        {update.isError && (
          <p role="alert" className="mt-3 rounded-xl bg-clay-50 px-3 py-2 text-sm text-clay-700">
            Could not save. Check the fields and try again.
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} loading={update.isPending}>
            Save
          </Button>
        </div>

        <p className="mt-3 text-xs text-ink-400">
          Changing the Portuguese does not regenerate its audio — that needs a new clip
          from the generator script.
        </p>
      </div>
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  lang,
  className = '',
}: {
  label: string
  value: string
  onChange: (v: string) => void
  lang?: string
  className?: string
}) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm font-medium text-ink-700">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        lang={lang}
        className="min-h-11 w-full rounded-xl bg-white px-3 text-base ring-1 ring-sand-200 focus:ring-2 focus:ring-brand-600"
      />
    </div>
  )
}
